import { Language, isLanguageSupported } from './index';

/**
 * Normalize language code by extracting only the language part
 * Examples: 'en-US' -> 'en', 'ro-RO' -> 'ro', 'de-DE' -> 'de'
 * @param langCode - Language code that might include region
 * @returns Normalized language code
 */
export const normalizeLanguageCode = (langCode: string): string => {
  return langCode.split('-')[0].toLowerCase().trim();
};

/**
 * Extract language code from URL pathname
 * Looks for patterns like /en/, /de/, /fr/ etc. in the URL
 * @param url - Full URL or pathname
 * @returns Language code if found, null otherwise
 */
export const getLanguageFromUrl = (url: string = window.location.pathname): Language | null => {
  // Common URL patterns:
  // /en/page -> 'en'
  // /lang/de/page -> 'de'  
  // /page?lang=fr -> handled separately
  // /page#lang=es -> handled separately
  
  // Extract path segments
  const pathSegments = url.split('/').filter(segment => segment.length > 0);
  
  // Check first segment
  if (pathSegments.length > 0) {
    const firstSegment = normalizeLanguageCode(pathSegments[0]);
    if (isLanguageSupported(firstSegment)) {
      return firstSegment;
    }
  }
  
  // Check for 'lang' segment pattern
  const langIndex = pathSegments.findIndex(segment => segment === 'lang');
  if (langIndex >= 0 && langIndex < pathSegments.length - 1) {
    const langCode = normalizeLanguageCode(pathSegments[langIndex + 1]);
    if (isLanguageSupported(langCode)) {
      return langCode;
    }
  }
  
  return null;
};

/**
 * Extract language code from URL query parameters
 * Looks for ?lang=en, ?language=de, ?locale=fr patterns
 * @param url - Full URL (optional, defaults to current URL)
 * @returns Language code if found, null otherwise
 */
export const getLanguageFromUrlParams = (url: string = window.location.href): Language | null => {
  try {
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;
    
    // Check common parameter names
    const paramNames = ['lang', 'language', 'locale', 'lng'];
    
    for (const paramName of paramNames) {
      const value = searchParams.get(paramName);
      if (value) {
        const normalizedValue = normalizeLanguageCode(value);
        if (isLanguageSupported(normalizedValue)) {
          return normalizedValue;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error parsing URL for language detection:', error);
    return null;
  }
};

/**
 * Get language from cookie
 * Checks for various cookie names that might store language preference
 * @returns Language code if found, null otherwise
 */
export const getLanguageFromCookie = (): Language | null => {
  try {
    const cookieNames = ['lang', 'language', 'locale', 'lng', 'i18n'];
    
    for (const cookieName of cookieNames) {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(cookieName + '='))
        ?.split('=')[1];
      
      if (cookieValue) {
        const normalizedValue = normalizeLanguageCode(cookieValue);
        if (isLanguageSupported(normalizedValue)) {
          return normalizedValue;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading language from cookies:', error);
    return null;
  }
};

/**
 * Get language from browser's navigator language
 * @returns Language code if supported, null otherwise
 */
export const getLanguageFromBrowser = (): Language | null => {
  try {
    // Get primary language from browser
    const browserLang = navigator.language || navigator.languages?.[0] || '';
    
    // Extract language code (before any region code like 'en-US' -> 'en')
    const langCode = normalizeLanguageCode(browserLang);
    
    if (isLanguageSupported(langCode)) {
      return langCode;
    }
    
    return null;
  } catch (error) {
    console.warn('Error getting language from browser:', error);
    return null;
  }
};

/**
 * Detect language using priority order:
 * 1. User config
 * 2. URL path segments
 * 3. URL query parameters  
 * 4. Language cookie
 * 5. Browser language
 * 6. Default fallback
 * 
 * @param userConfigLang - Language from user configuration
 * @param defaultLang - Default language fallback (default: 'ro')
 * @returns Detected language code
 */
export const detectLanguage = (
  userConfigLang?: string, 
  defaultLang: Language = 'ro'
): Language => {
  // 1. Check user config first
  if (userConfigLang) {
    const normalizedUserLang = normalizeLanguageCode(userConfigLang);
    if (isLanguageSupported(normalizedUserLang)) {
      return normalizedUserLang;
    }
  }
  
  // 2. Check URL path
  const urlLang = getLanguageFromUrl();
  if (urlLang) {
    return urlLang;
  }
  
  // 3. Check URL parameters
  const urlParamLang = getLanguageFromUrlParams();
  if (urlParamLang) {
    return urlParamLang;
  }
  
  // 4. Check cookies
  const cookieLang = getLanguageFromCookie();
  if (cookieLang) {
    return cookieLang;
  }
  
  // 5. Check browser language
  const browserLang = getLanguageFromBrowser();
  if (browserLang) {
    return browserLang;
  }
  
  // 6. Fall back to default
  return defaultLang;
};

/**
 * Save language preference to cookie
 * @param language - Language code to save
 * @param cookieName - Cookie name (default: 'lang')
 * @param expirationDays - Cookie expiration in days (default: 365)
 */
export const saveLanguageToCookie = (
  language: Language, 
  cookieName: string = 'lang',
  expirationDays: number = 365
): void => {
  try {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + expirationDays);
    
    document.cookie = `${cookieName}=${language}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax; ${window.location.protocol === 'https:' ? 'Secure;' : ''}`;
  } catch (error) {
    console.warn('Error saving language to cookie:', error);
  }
};
